import { useStripePaymentsEnabled } from "../hooks/useStripePaymentsEnabled";
import { LoadingButton } from "@mui/lab";
import { Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { LinearProgress, useGetOne, useGetRecordId, useRecordContext, useRefresh } from "react-admin";
import { getAuthenticatedBackendClient } from "../api/backend.api.js";
import { grants } from "../backoffice.access_control.js";
import { reportError } from "../backoffice.utils.js";
import { useDialog } from "../hooks/useDialog.js";
import { DialogCloseButton } from "../misc/DialogCloseButton.js";
import { FundingInstructions } from "../providers/backendUserProvider";
import type { Student } from "../providers/studentsProvider.js";
import { LabeledAndEditable } from "../misc/LabeledAndEditable.js";

function CreateFundingInstructionsDialog(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props;
  const student = useRecordContext<Student>();
  const [isLoading, setIsLoading] = useState(false);
  const refresh = useRefresh();

  const createFundingInstructions = useCallback(async () => {
    try {
      const backendClient = await getAuthenticatedBackendClient();
      setIsLoading(true);
      await backendClient.user.userFundingInstructionsRetrieve({ id: student.id, forceShow: true });
      setIsLoading(false);
      refresh();
      showSuccessToast("Kontodaten erfolgreich angelegt.");
    } catch (error) {
      reportError(`Failed to create funding instructions for student ${student?.id}`, error);
      showErrorToast("Fehler beim Anlegen der Kontodaten.");
    } finally {
      onClose();
    }
  }, [student]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Kontodaten für Überweisung anlegen</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <DialogContentText>
          Möchtest du für <strong>{student.name}</strong> die Kontodaten für Überweisungen anlegen?
        </DialogContentText>
        <Alert severity="warning" style={{ margin: "20px 0" }}>
          Bitte bedenke, dass diese Kontodaten nur für diesen Fahrschüler gültig sind und nicht an andere Fahrschüler
          weitergegeben werden dürfen, da es sonst zu Fehlbuchungen kommen kann.
        </Alert>
      </DialogContent>
      <DialogActions>
        <LoadingButton loading={isLoading} variant="contained" onClick={createFundingInstructions} autoFocus>
          Anlegen
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export function FundingInstructionsField() {
  const student = useRecordContext<Student>();
  const enabled = useStripePaymentsEnabled(student?.drivingSchoolId);
  return enabled ? <EnabledFundingInstructionsField /> : null;
}

function EnabledFundingInstructionsField() {
  const studentId = useGetRecordId() as string;
  const { data, isLoading, error } = useGetOne<FundingInstructions>("fundingInstructions", { id: studentId });
  const { dialogProps, openDialog } = useDialog("CreateFundingInstructionsDialog");
  if (isLoading) {
    return <LinearProgress timeout={0} />;
  }
  if (!data || error) {
    return (
      <Typography component="span" variant="body2">
        Fehler bei der Abfrage
      </Typography>
    );
  }
  if (!data.use_bank_transfer) {
    const disableCreate = !grants.has("FundingInstructions:create");
    return (
      <>
        <LabeledAndEditable label="" onClick={disableCreate ? undefined : openDialog} disableEdit={disableCreate}>
          <Typography component="span" variant="body2">
            Kunde ist nicht für Überweisung freigeschaltet
          </Typography>
        </LabeledAndEditable>
        {!disableCreate && <CreateFundingInstructionsDialog {...dialogProps} />}
      </>
    );
  }
  return (
    <Typography component="span" variant="body2">
      Name: {data.account_holder_name}
      <br />
      IBAN: {data.iban}
      <br />
      BIC: {data.bic}
      <br />
      Referenz: Bitte Rechnungsnummer angeben
    </Typography>
  );
}
